import { Subject, interval } from 'rxjs'
import { takeUntil, switchMap, filter } from 'rxjs/operators'

import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core'
import { AuthenticationService } from '@services/authentication.service'
import { OpsLaborEntry, OpsService } from '@services/ops.service'

interface BatchGroup {
    batch_id: string
    total_hours: number
    entries: OpsLaborEntry[]
}

@Component({
    selector: 'shared-my-shift-panel',
    templateUrl: './my-shift-panel.component.html',
    styleUrls: ['./my-shift-panel.component.css'],
    standalone: false
})
export class MyShiftPanelComponent implements OnInit, OnChanges, OnDestroy {
    @Input() siteId: string
    @Input() isOpen: boolean = false
    @Output() close = new EventEmitter<void>()

    private destroy$ = new Subject<void>()

    entries: OpsLaborEntry[] = []
    activeEntry: OpsLaborEntry | null = null
    loading = false
    todayTotalHours = 0
    batchGroups: BatchGroup[] = []

    /** Elapsed hours for the active (not yet clocked-out) entry */
    private activeElapsedHours = 0

    constructor(
        private _authenticationService: AuthenticationService,
        private _opsService: OpsService
    ) {}

    ngOnInit(): void {
        // Update elapsed time for active entry every 30 seconds
        interval(30_000).pipe(
            takeUntil(this.destroy$),
            filter(() => this.isOpen && this.activeEntry != null)
        ).subscribe(() => {
            this.recalculateActiveElapsed()
            this.calculateTodayTotal()
        })
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['isOpen'] && this.isOpen && this.siteId) {
            this.loadTodayEntries()
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next()
        this.destroy$.complete()
    }

    onBackdropClick(): void {
        this.close.emit()
    }

    /** Format decimal hours to "Xh Ym" display string */
    formatDuration(hours: number): string {
        if (hours <= 0) {
            return '0m'
        }
        const totalMinutes = Math.round(hours * 60)
        const h = Math.floor(totalMinutes / 60)
        const m = totalMinutes % 60

        if (h === 0) {
            return `${m}m`
        }
        if (m === 0) {
            return `${h}h`
        }
        return `${h}h ${m}m`
    }

    /** Get today's date range using local dates (avoids UTC off-by-one) */
    private getTodayRange(): { start: string; end: string } {
        const now = new Date()
        const year = now.getFullYear()
        const month = now.getMonth() // 0-indexed
        const day = now.getDate()

        // Build local start-of-day and end-of-day
        const startOfDay = new Date(year, month, day)
        const endOfDay = new Date(year, month, day, 23, 59, 59, 999)

        return {
            start: startOfDay.toISOString(),
            end: endOfDay.toISOString()
        }
    }

    private loadTodayEntries(): void {
        const userId = this._authenticationService.appMemberID
        if (!userId || !this.siteId) {
            return
        }

        this.loading = true
        const { start, end } = this.getTodayRange()

        this._opsService.getLaborEntries(this.siteId, userId, start, end).pipe(
            takeUntil(this.destroy$),
            switchMap(entries => {
                this.entries = entries
                // Also fetch the active clock-in to identify an ongoing entry
                return this._opsService.getActiveClockIn()
            }),
            takeUntil(this.destroy$)
        ).subscribe({
            next: (active) => {
                this.activeEntry = active
                this.recalculateActiveElapsed()
                this.groupByBatch()
                this.calculateTodayTotal()
                this.loading = false
            },
            error: () => {
                this.loading = false
            }
        })
    }

    /** Recalculate how long the active (not clocked out) entry has been running */
    private recalculateActiveElapsed(): void {
        if (!this.activeEntry) {
            this.activeElapsedHours = 0
            return
        }
        const clockIn = new Date(this.activeEntry.clock_in_ts)
        const now = new Date()
        this.activeElapsedHours = Math.max(0, (now.getTime() - clockIn.getTime()) / 3_600_000)
    }

    /** Sum completed durations + active elapsed time */
    private calculateTodayTotal(): void {
        let total = 0
        for (const entry of this.entries) {
            if (entry.clock_out_ts && entry.duration_hours != null) {
                total += entry.duration_hours
            }
        }
        total += this.activeElapsedHours
        this.todayTotalHours = total
    }

    /** Group entries by batch_id and sum hours per group */
    private groupByBatch(): void {
        const map = new Map<string, BatchGroup>()

        for (const entry of this.entries) {
            if (!entry.batch_id) {
                continue
            }
            const key = entry.batch_id
            let group = map.get(key)
            if (!group) {
                group = { batch_id: key, total_hours: 0, entries: [] }
                map.set(key, group)
            }
            group.entries.push(entry)
            group.total_hours += this.getEntryHours(entry)
        }

        this.batchGroups = Array.from(map.values())
    }

    /** Get hours for a single entry, using elapsed time for the active entry */
    private getEntryHours(entry: OpsLaborEntry): number {
        if (this.activeEntry && entry.id === this.activeEntry.id) {
            return this.activeElapsedHours
        }
        return entry.duration_hours ?? 0
    }
}
